/**
 * API Service Layer — Hub Bearer auth with automatic refresh on 401.
 */

import { env } from '../config';
import {
  clearStoredToken,
  getStoredRefreshToken,
  getStoredToken,
  getStoredUserEmail,
  setStoredSession,
} from '../auth';
import type {
  ApiResponse,
  AuthTokenPayload,
  AuthUser,
  PromptsListData,
  UsersListData,
  AdminKPIs,
  ListPromptsRequest,
  PromptDetails,
  PromptReportData,
  LeaderboardData,
  HubSkillsSyncResult,
  HubSkillsSyncStatus,
  HubSkillsSyncLogsData,
  ScoringRunsData,
  ScoringRunDetail,
  LastFetchData,
  OrgAnalytics,
  OrgQuestions,
  ArtifactAnalytics,
  MeAnalytics,
  MePatterns,
  ScoreMixData,
  LowScoreListData,
  AdminUserRow,
} from '../types';
import { toAuthUser } from '../types';

const AUTH_PUBLIC_PATHS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/oauth/callback',
];

const AUTH_NO_REFRESH_PATHS = ['/auth/logout'];

class ApiService {
  private baseUrl: string;
  private timeout: number;
  private refreshPromise: Promise<string | null> | null = null;

  constructor() {
    this.baseUrl = env.apiBaseUrl;
    this.timeout = env.apiTimeout;
  }

  private getAuthToken(): string | null {
    return getStoredToken();
  }

  private async tryRefreshToken(): Promise<string | null> {
    const refreshToken = getStoredRefreshToken();
    const email = getStoredUserEmail();
    if (!refreshToken || !email) {
      return null;
    }

    const accessToken = getStoredToken();
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ refresh_token: refreshToken, email }),
      });
      clearTimeout(timeoutId);

      const envelope = (await response.json()) as ApiResponse<AuthTokenPayload>;
      if (!response.ok || !envelope.success || !envelope.data) {
        return null;
      }

      const data = envelope.data;
      setStoredSession(data.access_token, data.refresh_token, data.user.email);
      return data.access_token;
    } catch {
      return null;
    }
  }

  private shouldSkipRefresh(endpoint: string): boolean {
    return (
      AUTH_PUBLIC_PATHS.some((p) => endpoint.includes(p)) ||
      AUTH_NO_REFRESH_PATHS.some((p) => endpoint.includes(p))
    );
  }

  /**
   * Authenticated fetch with automatic token refresh on 401.
   * Used for JSON and binary responses (e.g. PDF download).
   */
  private async fetchWithAuth(
    endpoint: string,
    options: RequestInit = {},
    isRetry: boolean = false
  ): Promise<Response> {
    const token = this.getAuthToken();
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });

    if (response.status === 401 && !isRetry && !this.shouldSkipRefresh(endpoint)) {
      if (!this.refreshPromise) {
        this.refreshPromise = this.tryRefreshToken().finally(() => {
          this.refreshPromise = null;
        });
      }
      const newToken = await this.refreshPromise;
      if (newToken) {
        return this.fetchWithAuth(endpoint, options, true);
      }
      this.redirectToLoginExpired();
      throw new Error('Session expired. Please sign in again.');
    }

    return response;
  }

  private redirectToLoginExpired(): void {
    clearStoredToken();
    const path = window.location.pathname;
    if (!path.startsWith('/login') && !path.startsWith('/auth/callback')) {
      window.location.assign('/login?expired=1');
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry: boolean = false
  ): Promise<ApiResponse<T>> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    const token = this.getAuthToken();

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      if (response.status === 204) {
        return { success: true, message: '', data: null as T };
      }

      const data = await response.json();

      if (
        response.status === 401 &&
        !isRetry &&
        !this.shouldSkipRefresh(endpoint)
      ) {
        if (!this.refreshPromise) {
          this.refreshPromise = this.tryRefreshToken().finally(() => {
            this.refreshPromise = null;
          });
        }
        const newToken = await this.refreshPromise;
        if (newToken) {
          return this.request<T>(endpoint, options, true);
        }
        this.redirectToLoginExpired();
        throw new Error(
          (data && data.message) || 'Session expired. Please sign in again.'
        );
      }

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data as ApiResponse<T>;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout');
        }
        throw error;
      }
      throw new Error('An unknown error occurred');
    }
  }

  /**
   * POST /auth/login
   */
  async login(email: string, password: string): Promise<ApiResponse<AuthTokenPayload>> {
    return this.request<AuthTokenPayload>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  /**
   * POST /auth/oauth/callback
   */
  async oauthCallback(code: string): Promise<ApiResponse<AuthTokenPayload>> {
    return this.request<AuthTokenPayload>('/auth/oauth/callback', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  /**
   * POST /auth/logout — uses stored tokens; does not trigger refresh on 401.
   */
  async logout(accessToken: string, refreshToken: string): Promise<void> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    try {
      await fetch(`${this.baseUrl}/auth/logout`, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * GET /auth/me
   */
  async me(): Promise<ApiResponse<AuthTokenPayload['user']>> {
    return this.request<AuthTokenPayload['user']>('/auth/me', {
      method: 'GET',
    });
  }

  /** Absolute URL for Hub Google OAuth redirect (browser navigates here). */
  getGoogleAuthUrl(): string {
    return `${this.baseUrl}/auth/google`;
  }

  /**
   * Get paginated list of prompts with optional filters
   * POST /list_prompts
   */
  async listPrompts(params: ListPromptsRequest = {}): Promise<ApiResponse<PromptsListData>> {
    return this.request<PromptsListData>('/list_prompts', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  /**
   * Get paginated list of users
   * GET /users
   */
  async getUsers(params?: {
    page?: number;
    per_page?: number;
    min_score?: number;
    max_score?: number;
    email?: string;
    full_list?: boolean;
    limit?: 'all';
  }): Promise<ApiResponse<UsersListData>> {
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.per_page) searchParams.append('per_page', params.per_page.toString());
    if (params?.min_score !== undefined) {
      searchParams.append('min_score', params.min_score.toString());
    }
    if (params?.max_score !== undefined) {
      searchParams.append('max_score', params.max_score.toString());
    }
    if (params?.email) searchParams.append('email', params.email);

    if (params?.full_list) {
      searchParams.append('full_list', 'true');
    } else if (params?.limit === 'all') {
      searchParams.append('limit', 'all');
    }

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/users?${queryString}` : '/users';

    return this.request<UsersListData>(endpoint, {
      method: 'GET',
    });
  }

  /**
   * Get admin KPIs
   * GET /admin_kpis
   */
  async getAdminKPIs(email?: string): Promise<ApiResponse<AdminKPIs>> {
    const endpoint = email ? `/admin_kpis?email=${encodeURIComponent(email)}` : '/admin_kpis';
    return this.request<AdminKPIs>(endpoint, {
      method: 'GET',
    });
  }

  private analyticsQuery(start?: string, end?: string, email?: string): string {
    const qs = new URLSearchParams();
    if (start) qs.set('start', start);
    if (end) qs.set('end', end);
    if (email) qs.set('email', email);
    const suffix = qs.toString();
    return suffix ? `?${suffix}` : '';
  }

  async getOrgAnalytics(start?: string, end?: string): Promise<ApiResponse<OrgAnalytics>> {
    return this.request<OrgAnalytics>(`/analytics/org${this.analyticsQuery(start, end)}`, {
      method: 'GET',
    });
  }

  async getArtifactAnalytics(start?: string, end?: string): Promise<ApiResponse<ArtifactAnalytics>> {
    return this.request<ArtifactAnalytics>(`/analytics/artifacts${this.analyticsQuery(start, end)}`, {
      method: 'GET',
    });
  }

  async getMeAnalytics(
    start?: string,
    end?: string,
    emails?: string[]
  ): Promise<ApiResponse<MeAnalytics>> {
    const qs = new URLSearchParams();
    if (start) qs.set('start', start);
    if (end) qs.set('end', end);
    (emails ?? []).forEach((email) => qs.append('emails', email));
    const suffix = qs.toString();
    return this.request<MeAnalytics>(`/analytics/me${suffix ? `?${suffix}` : ''}`, {
      method: 'GET',
    });
  }

  async getOrgQuestions(start?: string, end?: string): Promise<ApiResponse<OrgQuestions>> {
    return this.request<OrgQuestions>(`/analytics/org-questions${this.analyticsQuery(start, end)}`, {
      method: 'GET',
    });
  }

  async getScoreMix(start?: string, end?: string): Promise<ApiResponse<ScoreMixData>> {
    return this.request<ScoreMixData>(`/analytics/score-mix${this.analyticsQuery(start, end)}`, {
      method: 'GET',
    });
  }

  async getLowScores(): Promise<ApiResponse<LowScoreListData>> {
    return this.request<LowScoreListData>('/analytics/low-scores', { method: 'GET' });
  }

  async listAdminUsers(query?: string): Promise<ApiResponse<{ users: AdminUserRow[] }>> {
    const qs = query ? `?q=${encodeURIComponent(query)}` : '';
    return this.request<{ users: AdminUserRow[] }>(`/admin/users${qs}`, { method: 'GET' });
  }

  async patchAdminUserRole(
    userId: number,
    role: AdminUserRow['role']
  ): Promise<ApiResponse<AdminUserRow>> {
    return this.request<AdminUserRow>(`/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  async getMePatterns(start?: string, end?: string): Promise<ApiResponse<MePatterns>> {
    return this.request<MePatterns>(`/analytics/me/patterns${this.analyticsQuery(start, end)}`, {
      method: 'GET',
    });
  }

  /**
   * Get detailed prompt information by ID
   * GET /prompts/:prompt_id
   */
  async getPromptDetails(promptId: string): Promise<ApiResponse<PromptDetails>> {
    return this.request<PromptDetails>(`/prompts/${encodeURIComponent(promptId)}`, {
      method: 'GET',
    });
  }

  /**
   * Windowed leaderboard
   * GET /leaderboard?start=&end=
   */
  async getLeaderboard(start?: string, end?: string): Promise<ApiResponse<LeaderboardData>> {
    return this.request<LeaderboardData>(`/leaderboard${this.analyticsQuery(start, end)}`, {
      method: 'GET',
    });
  }

  /**
   * GET /reports/prompt-summary/data
   */
  async getPromptReportData(
    start: string,
    end: string,
    includeEmails: string[] = [],
    excludeEmails: string[] = []
  ): Promise<ApiResponse<PromptReportData>> {
    const qs = new URLSearchParams({ start, end });
    includeEmails.forEach((e) => qs.append('include_emails', e));
    excludeEmails.forEach((e) => qs.append('exclude_emails', e));
    return this.request<PromptReportData>(`/reports/prompt-summary/data?${qs.toString()}`, {
      method: 'GET',
    });
  }

  /**
   * GET /reports/prompt-summary.pdf — binary PDF
   */
  async downloadPromptReportPdf(
    start: string,
    end: string,
    includeEmails: string[] = [],
    excludeEmails: string[] = []
  ): Promise<Blob> {
    const qs = new URLSearchParams({ start, end });
    includeEmails.forEach((e) => qs.append('include_emails', e));
    excludeEmails.forEach((e) => qs.append('exclude_emails', e));
    const endpoint = `/reports/prompt-summary.pdf?${qs.toString()}`;
    const controller = new AbortController();
    const timeoutMs = 120000;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await this.fetchWithAuth(endpoint, {
        method: 'GET',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok) {
        let message = `HTTP error ${response.status}`;
        try {
          const errBody = await response.json();
          if (errBody?.message) message = errBody.message;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }
      return await response.blob();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Report download timed out');
      }
      throw error;
    }
  }

  /**
   * POST /scheduler/sync_hub_skills/run — start Hub skills sync in the background
   */
  async runHubSkillsSync(): Promise<ApiResponse<HubSkillsSyncResult>> {
    return this.request<HubSkillsSyncResult>('/scheduler/sync_hub_skills/run', {
      method: 'POST',
    });
  }

  /**
   * GET /scheduler/sync_hub_skills/status
   */
  async getHubSkillsSyncStatus(): Promise<ApiResponse<HubSkillsSyncStatus>> {
    return this.request<HubSkillsSyncStatus>('/scheduler/sync_hub_skills/status', {
      method: 'GET',
    });
  }

  /**
   * GET /scheduler/sync_hub_skills/logs?page=&per_page=
   */
  async getHubSkillsSyncLogs(
    page: number = 1,
    perPage: number = 20
  ): Promise<ApiResponse<HubSkillsSyncLogsData>> {
    const qs = new URLSearchParams({
      page: String(page),
      per_page: String(perPage),
    });
    return this.request<HubSkillsSyncLogsData>(
      `/scheduler/sync_hub_skills/logs?${qs.toString()}`,
      { method: 'GET' }
    );
  }

  async getScoringRuns(limit: number = 30): Promise<ApiResponse<ScoringRunsData>> {
    return this.request<ScoringRunsData>(`/scoring/runs?limit=${limit}`, {
      method: 'GET',
    });
  }

  async getScoringRun(runId: string): Promise<ApiResponse<ScoringRunDetail>> {
    return this.request<ScoringRunDetail>(`/scoring/runs/${encodeURIComponent(runId)}`, {
      method: 'GET',
    });
  }

  async getLastFetch(): Promise<ApiResponse<LastFetchData>> {
    return this.request<LastFetchData>('/scheduler/last_fetch', { method: 'GET' });
  }
}

export const apiService = new ApiService();

export function applyAuthSession(data: AuthTokenPayload): AuthUser {
  setStoredSession(data.access_token, data.refresh_token, data.user.email);
  return toAuthUser(data.user);
}

export default apiService;
